import React, { useEffect, useRef, useState } from "react";
import { Button } from "../../../components/shadcn/ui/button";
import {
  ProviderTagOutcome,
  ProviderTagVocabulary,
  TwlQPreview,
  TwlQToday,
  TwlQResult,
  TwlQResultCode,
  providerApi,
} from "../providerApi";

/**
 * Provider tags as chips (owner ruling, Isaiah Cotto, 2026-10-21: "I need the
 * provider tags clickable rather than entered/written so once a resident is
 * marked TWL Q it goes straight to the PAs"). docs/provider/TWLQ_CLICK.md.
 *
 * Every chip is a real toggle button: a visible text label, `aria-pressed`, a
 * mark that is a glyph and not only a colour, 44px tall, reachable by Tab and
 * pressed with Enter or Space. The vocabulary is the server's
 * (`provenance.providerTags`); nothing here invents a word.
 */

/** The comparison form the server uses: trimmed, inner spaces collapsed, upper case. */
export function normTag(w: string): string {
  return String(w || "").trim().replace(/\s+/g, " ").toUpperCase();
}

/** Does `tags` carry every word in `words`? */
export function hasAll(tags: string[], words: string[]): boolean {
  const s = new Set(tags.map(normTag));
  return words.length > 0 && words.every((w) => s.has(normTag(w)));
}

/** `tags` without `words`, by the server's comparison. */
export function without(tags: string[], words: string[]): string[] {
  const drop = new Set(words.map(normTag));
  return tags.filter((t) => !drop.has(normTag(t)));
}

/** One more attempt key. Kept across a lost answer, replaced after a refusal. */
export function newMarkKey(): string {
  const c = typeof window !== "undefined" ? window.crypto : undefined;
  if (c && typeof c.randomUUID === "function") return `twlq-${c.randomUUID()}`;
  return `twlq-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function TagChip({
  label,
  pressed,
  disabled,
  onClick,
  describedBy,
}: {
  label: string;
  pressed: boolean;
  disabled?: boolean;
  onClick: () => void;
  describedBy?: string;
}) {
  return (
    <button
      type="button"
      className={`fw-tag-chip${pressed ? " is-on" : ""}`}
      aria-pressed={pressed}
      aria-describedby={describedBy}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="fw-tag-mark" aria-hidden="true">
        {pressed ? "✓" : "+"}
      </span>
      <span>{label}</span>
    </button>
  );
}

/**
 * The working marks: plain toggles, staged and saved with "Save manifest
 * changes" through the ordinary PATCH. A word the resident carries that is not
 * in the vocabulary is shown under "Other marks on file" and can only be
 * removed (the server keeps it until then).
 */
export function TagChipGroups({
  vocabulary,
  tags,
  onToggle,
  disabled,
}: {
  vocabulary: ProviderTagVocabulary | undefined;
  tags: string[];
  onToggle: (word: string) => void;
  disabled: boolean;
}) {
  const on = new Set(tags.map(normTag));
  // TWL and QUALIFIED are not plain chips (they arrive through TWL Q), so a resident who carries them sees them
  // here, where they can be removed: removing them never takes back a lead.
  const known = new Set<string>((vocabulary?.groups || []).flatMap((g) => g.words.map(normTag)));
  const others = tags.filter((t) => !known.has(normTag(t)));
  return (
    <div className="fw-tag-groups">
      {!vocabulary && (
        <p className="fw-muted">
          The tag list is not available from the server yet, so tags can only
          be removed here.
        </p>
      )}
      {(vocabulary?.groups || []).map((g) => (
        <fieldset key={g.key} className="fw-tag-group">
          <legend>{g.label}</legend>
          <div className="fw-tag-row">
            {g.words.map((w) => (
              <TagChip
                key={w}
                label={w}
                pressed={on.has(normTag(w))}
                disabled={disabled}
                onClick={() => onToggle(w)}
              />
            ))}
          </div>
        </fieldset>
      ))}
      {others.length > 0 && (
        <fieldset className="fw-tag-group">
          <legend>Other marks on file</legend>
          <p className="fw-muted" id="fw-tags-legacy-help">
            Marks that are not in the lists above (older marks, and the TWL and
            QUALIFIED words a TWL Q adds). Tap one to remove it, then save.
            Removing TWL or QUALIFIED does not take back a lead.
          </p>
          <div className="fw-tag-row">
            {others.map((w) => (
              <TagChip
                key={w}
                label={w}
                pressed
                disabled={disabled}
                onClick={() => onToggle(w)}
                describedBy="fw-tags-legacy-help"
              />
            ))}
          </div>
        </fieldset>
      )}
    </div>
  );
}

const RESULT_HEADLINE: Record<TwlQResultCode, string> = {
  ASSIGNED: "Sent to the PA",
  HELD_ON_DESK: "Held on the lead desk",
  UNASSIGNED: "Waiting on the lead desk",
  ALREADY_A_LEAD: "Already a lead",
  RESIDENT_HAS_ANOTHER_PA: "Already with another PA",
  NOT_A_NEW_LEAD: "Not a new lead",
  RECORDED: "Recorded",
};

/**
 * The TWL Q group: the five outcomes the business writes on the TWL Q tally.
 *
 * A chip is pressed when TODAY'S review line records that outcome for this
 * resident (the server's `provenance.twlQ.today`), never because an earlier
 * visit left TWL/QUALIFIED in the tags: a resident tagged TWL Q last month is
 * markable today. Each unpressed chip asks before it acts; TWL Q names the PA it
 * goes to (the server's word, read with the day sheet) or says why there is
 * none, and the answer is said in words afterwards. A pressed chip says what is
 * already recorded and does nothing: taking back a lead is the PA's or the
 * office's act, and removing tag words is the tag list's.
 */
export function TwlQOutcomes({
  vocabulary,
  preview,
  today,
  residentName,
  facilityName,
  facilityId,
  patientId,
  offline,
  disabled,
  onBusy,
  onMarked,
}: {
  vocabulary: ProviderTagVocabulary | undefined;
  preview: TwlQPreview | undefined;
  today: TwlQToday | null;
  residentName: string;
  facilityName: string;
  facilityId: string;
  patientId: string;
  offline: boolean;
  disabled: boolean;
  onBusy: (busy: boolean) => void;
  onMarked: (result: TwlQResult) => void;
}) {
  const [confirming, setConfirming] = useState<ProviderTagOutcome | null>(null);
  const [recorded, setRecorded] = useState<ProviderTagOutcome | null>(null);
  const [mine, setMine] = useState<ProviderTagOutcome["key"] | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<TwlQResult | null>(null);
  const [key, setKey] = useState(newMarkKey);
  const confirmRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // Focus moves to the confirm button, so a keyboard user lands on the choice the click asked for.
    const first = confirming && confirmRef.current ? confirmRef.current.querySelector("button") : null;
    if (first) (first as HTMLButtonElement).focus();
  }, [confirming]);

  const outcomes = vocabulary?.outcomes || [];
  const allowed = Boolean(preview && preview.allowed);
  if (!outcomes.length) return null;
  // Today's outcome: what this screen just recorded, else what the server read with the sheet.
  const todayKey = mine || (today ? today.outcome : null);

  const confirmText = (o: ProviderTagOutcome): string => {
    // Medicaid pending is the PAs' to follow up (ruling 2026-10-27): recorded, not a lead, and the building's PA is told.
    if (!o.sends_to_pa && o.key === 'TWL_MEDICAID_PENDING') {
      return preview?.pa
        ? `Record ${o.label} for ${residentName} on today's manifest review. It is not sent to a PA as a lead, but the PA for ${facilityName}, ${preview.pa.name}, is told.`
        : `Record ${o.label} for ${residentName} on today's manifest review. It is not sent to a PA as a lead, and no PA is set for this building, so nobody is told.`;
    }
    if (!o.sends_to_pa) {
      return `Record ${o.label} for ${residentName} on today's manifest review. It is not sent to a PA.`;
    }
    if (preview?.pa) {
      return `Mark TWL Q: this sends ${residentName} to the PA for ${facilityName}: ${preview.pa.name}.`;
    }
    return `Mark TWL Q for ${residentName} at ${facilityName}. ${preview?.words || "No PA is assigned to this building, so it goes to the unassigned queue on the lead desk."}`;
  };

  const go = async () => {
    if (!confirming || sending || offline) return;
    const o = confirming;
    setSending(true);
    onBusy(true);
    setError("");
    try {
      const r = await providerApi.markTwlQ(facilityId, patientId, {
        outcome: o.word,
        idempotency_key: key,
      });
      if (r.success && r.data) {
        setResult(r.data);
        setConfirming(null);
        setKey(newMarkKey());
        // A refused lead (already sold) recorded nothing today.
        if (r.data.result !== "NOT_A_NEW_LEAD") setMine(r.data.outcome);
        onMarked(r.data);
        return;
      }
      if (r.status === undefined && !r.code) {
        // Never reached, or the answer was lost: the same key, so trying again cannot send twice.
        setError(
          "eMerge did not confirm the mark. Nothing is shown as sent. Try again; trying again will not send it twice.",
        );
      } else {
        setError(r.message || r.error || "The mark was not recorded. Tell the office.");
        setKey(newMarkKey());
      }
    } catch {
      setError(
        "eMerge did not confirm the mark. Nothing is shown as sent. Try again; trying again will not send it twice.",
      );
    } finally {
      setSending(false);
      onBusy(false);
    }
  };

  return (
    <section className="fw-twlq" aria-labelledby={`fw-twlq-title-${patientId}`}>
      <h3 className="fw-replacement-title" id={`fw-twlq-title-${patientId}`}>
        TWL Q
      </h3>
      <p className="fw-muted" id={`fw-twlq-help-${patientId}`}>
        {allowed
          ? "TWL Q sends the resident to the building's PA as a lead. Medicaid pending records it and tells the PA, who follows up. Private pay, DNQ and Hospice are recorded only. A pressed outcome is the one recorded today."
          : "You cannot mark TWL Q for this building."}
      </p>
      <div className="fw-tag-row" role="group" aria-label="TWL Q outcomes">
        {outcomes.map((o) => {
          const pressed = todayKey === o.key;
          return (
            <TagChip
              key={o.key}
              label={o.label}
              pressed={pressed}
              describedBy={`fw-twlq-help-${patientId}`}
              disabled={disabled || sending || !allowed || offline}
              onClick={() => {
                setError("");
                if (pressed) {
                  setConfirming(null);
                  setRecorded(o);
                  return;
                }
                setRecorded(null);
                setConfirming(o);
              }}
            />
          );
        })}
      </div>
      {recorded && !confirming && (
        <p className="fw-muted" role="note">
          {recorded.label} is already recorded for {residentName} today.
          {recorded.sends_to_pa
            ? " Marking it again sends nothing new. To take back a lead, tell the PA or the office; removing the TWL and QUALIFIED tags below does not take it back."
            : " To change it, choose another outcome."}
        </p>
      )}
      {confirming && (
        <div ref={confirmRef} className="fw-confirm" role="group" aria-label="Confirm" aria-describedby={`fw-twlq-confirm-${patientId}`}>
          <p id={`fw-twlq-confirm-${patientId}`}>{confirmText(confirming)}</p>
          <div className="fw-actions">
            <Button
              className="fw-primary"
              type="button"
              disabled={sending || offline}
              onClick={go}
            >
              {sending ? "Sending…" : confirming.sends_to_pa ? "Send to PA" : "Record"}
            </Button>
            <Button
              variant="outline"
              type="button"
              disabled={sending}
              onClick={() => {
                setConfirming(null);
                setError("");
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      {error && (
        <p role="alert" className="fw-error">
          {error}
        </p>
      )}
      <div role="status" className="fw-save-status fw-twlq-result">
        {sending ? (
          "Sending to eMerge…"
        ) : result ? (
          <>
            <strong>{RESULT_HEADLINE[result.result] || "Recorded"}.</strong>{" "}
            {result.message}
            {result.held_by && result.held_by.length > 0 && (
              <ul className="fw-twlq-held">
                {result.held_by.map((h) => (
                  <li key={h.code}>{h.what}</li>
                ))}
              </ul>
            )}
          </>
        ) : (
          ""
        )}
      </div>
      {offline && (
        <p className="fw-muted">You are offline. TWL Q can be marked once you are connected.</p>
      )}
    </section>
  );
}
